// Type-safe API client. All methods return Promise<T> and throw ApiError on
// non-2xx responses. 401 auto-redirects to /login.

import type { Node, Container, Service, DeployRecord, DomainRecord } from './types'

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (res.status === 401) {
    if (!location.pathname.startsWith('/login')) {
      window.location.href = '/login'
    }
    throw new ApiError(401, 'unauthorized')
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(res.status, (body as { error?: string } | null)?.error ?? res.statusText, body)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

// ─── Types ────────────────────────────────────────────────────────────────

export interface User {
  id: number
  email: string
  name: string
}

export interface Stats {
  users: number
  active_sessions: number
}

export interface VersionInfo {
  commit: string
  built_at: string
}

// ─── API ──────────────────────────────────────────────────────────────────

export const api = {
  // Auth
  login: (email: string, password: string) =>
    request<User>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  me: () => request<User>('/auth/me'),

  // Meta
  health: () => request<{ ok: boolean }>('/api/health'),
  version: () => request<VersionInfo>('/api/version'),
  stats: () => request<Stats>('/api/stats'),

  // Nodes
  listNodes: () => request<Node[]>('/api/admin/nodes'),

  // Containers
  listContainers: (nodeId: number) =>
    request<Container[]>(`/api/admin/nodes/${nodeId}/containers`),
  restartContainer: (id: string, nodeId: number) =>
    request<void>(`/api/admin/containers/${id}/restart?node=${nodeId}`, {
      method: 'POST',
    }),
  stopContainer: (id: string, nodeId: number) =>
    request<void>(`/api/admin/containers/${id}/stop?node=${nodeId}`, {
      method: 'POST',
    }),
  startContainer: (id: string, nodeId: number) =>
    request<void>(`/api/admin/containers/${id}/start?node=${nodeId}`, {
      method: 'POST',
    }),
  containerLogs: (id: string, nodeId: number, tail = 100) =>
    request<{ logs: string }>(
      `/api/admin/containers/${id}/logs?node=${nodeId}&tail=${tail}`,
    ),

  // Services
  listServices: () => request<Service[]>('/api/admin/services'),
  getService: (id: number) => request<Service>(`/api/admin/services/${id}`),
  createService: (data: Partial<Service>) =>
    request<Service>('/api/admin/services', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateService: (id: number, data: Partial<Service>) =>
    request<Service>(`/api/admin/services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteService: (id: number) =>
    request<void>(`/api/admin/services/${id}`, { method: 'DELETE' }),
  syncServiceEnv: (id: number) =>
    request<{ synced: number }>(`/api/admin/services/${id}/sync-env`, { method: 'POST' }),
  listServiceDeploys: (id: number, limit = 20) =>
    request<DeployRecord[]>(`/api/admin/services/${id}/deploys?limit=${limit}`),
  deployService: (id: number) =>
    request<{ deploy_id: number; status: string }>(
      `/api/admin/services/${id}/deploy`,
      { method: 'POST' },
    ),

  // Deploys
  listDeploys: (limit = 50) =>
    request<DeployRecord[]>(`/api/admin/deploys?limit=${limit}`),
  listPendingDeploys: () =>
    request<DeployRecord[]>('/api/admin/deploys/pending'),
  approveDeploy: (id: number) =>
    request<{ status: string }>(`/api/admin/deploys/${id}/approve`, {
      method: 'POST',
    }),
  skipDeploy: (id: number) =>
    request<{ status: string }>(`/api/admin/deploys/${id}/skip`, {
      method: 'POST',
    }),
  redeploy: (id: number) =>
    request<{ deploy_id: number; status: string }>(
      `/api/admin/deploys/${id}/redeploy`,
      { method: 'POST' },
    ),

  // Domains
  listDomains: () => request<DomainRecord[]>('/api/admin/domains'),
  createDomain: (data: { fqdn: string; service_id?: number; ttl?: number; redirect_to?: string; auth_token?: string }) =>
    request<DomainRecord>('/api/admin/domains', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  deleteDomain: (id: number) =>
    request<void>(`/api/admin/domains/${id}`, { method: 'DELETE' }),
  updateDomain: (id: number, data: { service_id?: number | null; redirect_to?: string; auth_token?: string }) =>
    request<DomainRecord>(`/api/admin/domains/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  generateDomainConfig: (id: number, query?: string) =>
    request<{ config: string; fqdn: string; container: string; upstream: string }>(
      `/api/admin/domains/${id}/generate-config${query ? '?' + query : ''}`, { method: 'POST' }),
  getDomainConfig: (id: number) =>
    request<{ config: string; fqdn: string }>(`/api/admin/domains/${id}/config`),
  putDomainConfig: (id: number, config: string) =>
    request<{ status: string; applied: string; reason?: string }>(`/api/admin/domains/${id}/config`, {
      method: 'PUT',
      body: JSON.stringify({ config }),
    }),
  refreshDomain: (id: number) =>
    request<DomainRecord>(`/api/admin/domains/${id}/refresh`, {
      method: 'POST',
    }),

  // Networks
  listNetworks: () => request<{ node_name: string; networks: unknown[] | null }[]>('/api/admin/networks'),

  // Settings
  getSettings: () => request<{
    telegram: { enabled: string; bot_token: string; chat_id: string }
    github: { token: string }
  }>('/api/admin/settings'),
  putSettings: (data: {
    telegram?: { enabled?: string; bot_token: string; chat_id: string }
    github?: { token: string }
  }) =>
    request<void>('/api/admin/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
}
